// ONE VOCABULARY FOR NUMBERS A CUSTOMER READS.
//
// Forge f3: nothing unrendered reaches a reader — bytes are humanised against
// the cap they are measured by. These two functions were written for the Admin
// page (E08-t4) and are lifted out of it verbatim by E14-t3, because Home now
// shows the SAME two totals per knowledge base (E14 q2, answered "Both on
// Home"). Two copies would eventually disagree about what "50 MB" means, and
// the number a reader is shown has to match the number they were promised.
//
// Contract: vault/Services/Tenancy and Guards.md q4 — platform/src/caps.ts pins
// 50 MiB and the vault calls it "50 MB", so the units below are BINARY under
// the vault's own labels. That mismatch is deliberate and documented there; it
// is not a rounding bug to be tidied away here.

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** 1536 -> "1.5 KB". Binary steps, one decimal below 10, none above. */
export function humanBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded =
    Number.isInteger(value) || value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString('en-US')} ${BYTE_UNITS[unit]}`;
}

/** 2000 -> "2,000". A cap nobody can read at a glance is a cap nobody respects.
 *  The locale is PINNED: the same render must read the same in every browser
 *  and in every test. */
export function humanCount(count: number): string {
  return count.toLocaleString('en-US');
}

/**
 * An ISO timestamp as a stable day — "2026-08-02".
 *
 * DELIBERATELY NOT LOCALE-FORMATTED, for the same reason `humanCount` pins its
 * locale: the same render must read the same in every browser and in every test.
 * A string that is not a date is returned UNCHANGED rather than turned into
 * "Invalid Date", because the control plane's own value is more useful to a
 * reader than this function's opinion of it.
 *
 * Dashboards.tsx used to hold a private copy of this (E14 t3 left it for t6);
 * it now reads this one, so a timestamp is formatted in exactly one place.
 */
export function isoDay(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toISOString().slice(0, 10);
}

/** The designs' `relative_short` grammar (2026-08-07 audit): "41m ago",
 *  "13h ago", "2d ago" — and the ISO day once a month has passed, because
 *  "370d ago" is a number nobody wanted. The design system's Timestamp is not
 *  used for this on purpose: its ESM import graph breaks under vitest (the
 *  E22 lesson), and a relative time is one division, not a component. */
export function relShort(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const minutes = Math.round((now - then) / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days <= 31) return `${days}d ago`;
  return isoDay(iso);
}
